import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@arcadeai/design-system/components/ui/atoms/collapsible";
import type {
  ConfigFormat,
  McpAuthType,
  McpClientId,
} from "@arcadeai/ui-kit/mcp-gateway-connect";
import {
  ConfigDisplay,
  canUseDeepLink,
  MCPInstallButton,
} from "@arcadeai/ui-kit/mcp-gateway-connect";
import { ChevronRight } from "lucide-react";
import type { JSX } from "react";

export type ConnectGatewayShape = {
  name: string;
  slug: string;
  auth_type: McpAuthType;
};

type ClientConnectDetailPanelProps = {
  clientId: McpClientId;
  enginePublicUrl: string;
  gateway: ConnectGatewayShape;
  onConfigCopy: (clientId: McpClientId, format: ConfigFormat) => void;
  onDeepLinkClick: (clientId: McpClientId, gatewaySlug: string) => void;
  userId: string | undefined;
};

export function ClientConnectDetailPanel({
  clientId,
  enginePublicUrl,
  gateway,
  onConfigCopy,
  onDeepLinkClick,
  userId,
}: ClientConnectDetailPanelProps): JSX.Element {
  const showInstall = canUseDeepLink(clientId, gateway.auth_type);

  const configBlock = (
    <div className="rounded-lg border border-border/50 bg-muted/30 p-4">
      <ConfigDisplay
        authType={gateway.auth_type}
        clientId={clientId}
        enginePublicUrl={enginePublicUrl}
        gatewayName={gateway.name}
        onCopy={onConfigCopy}
        slug={gateway.slug}
        userId={userId}
      />
    </div>
  );

  if (showInstall) {
    return (
      <div className="space-y-4">
        <MCPInstallButton
          clientId={clientId}
          enginePublicUrl={enginePublicUrl}
          gatewayName={gateway.name}
          gatewaySlug={gateway.slug}
          onDeepLinkClick={onDeepLinkClick}
        />

        <Collapsible>
          <CollapsibleTrigger className="group/button inline-flex h-7 w-full items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 font-medium text-muted-foreground text-xs transition-colors hover:bg-muted hover:text-foreground">
            <ChevronRight
              aria-hidden
              className="size-3.5 shrink-0 transition-transform duration-200 group-aria-expanded/button:rotate-90"
            />
            Quick install not working? Set up manually
          </CollapsibleTrigger>
          <CollapsibleContent className="overflow-hidden data-closed:animate-collapsible-up data-open:animate-collapsible-down">
            <div className="h-(--collapsible-panel-height) pt-3 data-ending-style:h-0 data-starting-style:h-0">
              {configBlock}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  }

  return configBlock;
}
